import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { get, size } from 'global/lib/lodash';
import { ACCESS_ALERT_QUERY, ACCESS_ALERT_MESSAGE, getSmartRedirectFor } from 'global/utils/loginRedirect';

// PFM-2565: Detect when an authenticated user has navigated to deep-link content
// that they don't actually have access to (or that doesn't exist), and redirect
// them to the corresponding base page with an access-alert query param so the
// base page can show the standard alert.
//
// Usage: pass the result of `useRetrieveData(...)` (or any { data, loading, error })
// for the "primary" resource that the page depends on.
//
// Behavior:
//   - If loading is true: do nothing
//   - If error is set: this signals a 4xx (forbidden / not-found) — redirect with alert
//   - If data is empty (no record returned): redirect with alert
const useAccessRedirect = (primaryResource, options = {}) => {
  const history = useHistory();
  const location = useLocation();
  const { skip = false, requireData = true } = options;
  // PFM-2547: in a hashed share-link session we route reads through /sharehash/v1
  // and the normal access-permission model doesn't apply, so we must not run the
  // standard access redirect (it can't tell "no access" from "loading mid-redeem").
  // PFM-2640: but once we're on the canonical path the hash token has propagated,
  // so an error here means the temp token redeemed yet the underlying resource is
  // gone — i.e. the shared content was deleted.
  const hashViewHasToken = useSelector((s) => !!s?.hashAuth?.hashToken);
  const hashScopeKind = useSelector((s) => s?.hashAuth?.scope?.kind);

  const { data, loading, error } = primaryResource || {};

  // PFM-2640: when the shared *assessment* itself is gone we DON'T redirect — we
  // signal the host page to render an in-place "deleted" message and keep the
  // viewer where they are. See the dedicated branch below for why.
  const [shareDeleted, setShareDeleted] = useState(false);

  useEffect(() => {
    if (skip) return;
    if (loading) return;

    // The hook may report data as either an array (collection) or a single object.
    const isArr = Array.isArray(data);
    const hasData = isArr ? size(data) > 0 : !!data && (typeof data !== 'object' || size(Object.keys(data)) > 0);

    const shouldRedirect = !!error || (requireData && !hasData);
    if (!shouldRedirect) {
      // Resource resolved fine — clear any stale deleted flag (e.g. after update()).
      if (shareDeleted) setShareDeleted(false);
      return;
    }

    if (hashViewHasToken) {
      // PFM-2640: in a hashed share-link session the temp token has redeemed, so a
      // 404/empty here means the shared content was deleted (not "no access").
      //
      // For ASSESSMENT scope, show the deleted message IN PLACE for both anonymous
      // and logged-in-without-normal-access viewers. We must NOT dispatch
      // HASH_AUTH_CLEAR or redirect: clearing the hash token leaves an anonymous
      // viewer with no session on a non-share path, which trips the App.js
      // anonymous-logout guard (hard /logout navigation) and drops the message;
      // and AccessAlertSnack only mounts for logged-in users, so an anonymous
      // viewer would never see a list-page snackbar anyway. Keeping the token and
      // staying put lets the host page render the in-place copy.
      if (hashScopeKind === 'assessment') {
        if (!shareDeleted) setShareDeleted(true);
        return;
      }

      // PFM-2641: a shared Library document renders inside DocumentList, whose
      // edition read (/sharehash/v1/editions/:id) and the document's own
      // edition-documents read may 404/500 on first load until the backend serves
      // those resources under /sharehash/v1 (documented backend dependency). Those
      // first-load failures are NOT "the shared content was deleted", so for
      // document scope we must NOT tear down the hash session (HASH_AUTH_CLEAR) —
      // doing so drops the only auth the anonymous viewer has and lands them on the
      // login screen (the original ticket symptom). Leave the viewer pinned to the
      // document page; the body simply stays empty until the backend catches up.
      if (hashScopeKind === 'document') return;
      // Any other hashed scope (e.g. task): fall through to the standard
      // redirect-with-alert below.
    }

    const target = getSmartRedirectFor(get(location, 'pathname', ''));
    if (!target) return;

    const sep = target.indexOf('?') === -1 ? '?' : '&';
    history.replace(`${target}${sep}${ACCESS_ALERT_QUERY}=1`);
  }, [skip, hashViewHasToken, hashScopeKind, loading, error, data, location.pathname, history, shareDeleted]);

  return { shareDeleted };
};

export { ACCESS_ALERT_QUERY, ACCESS_ALERT_MESSAGE };
export default useAccessRedirect;
